import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Shield, Key, Database, AlertTriangle, Lock, Server, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher';

const InformationSecurityPolicyEN = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Information Security Policy | EasyFinder</title>
        <meta name="description" content="EasyFinder Information Security Policy: Password Policy, Disaster Recovery, and Data Breach Response." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="-ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <LanguageSwitcher frPath="/information-security-policy" enPath="/information-security-policy-en" />
          </div>

          <div className="space-y-6">
            {/* Header */}
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-3xl font-bold">Information Security Policy</h1>
              <p className="text-muted-foreground">EasyFinder - SAS with €1,000 share capital</p>
              <div className="flex items-center justify-center gap-2">
                <Badge variant="outline">Version 1.0</Badge>
                <Badge variant="secondary">Last updated: December 2024</Badge>
              </div>
            </div>

            <Separator />

            {/* Introduction */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  1. Introduction and Objectives
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  This policy defines the security measures implemented by EasyFinder to protect 
                  our users' data. It applies to all EasyFinder systems, employees, and sub-processors.
                </p>
                <h4>Objectives:</h4>
                <ul>
                  <li>Protect data confidentiality, integrity, and availability</li>
                  <li>Ensure GDPR compliance and Google API Limited Use requirements</li>
                  <li>Maintain user trust</li>
                </ul>
              </CardContent>
            </Card>

            {/* Password Policy */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-primary" />
                  2. Password Policy
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  EasyFinder enforces password requirements aligned with security best practices,
                  with a real-time strength indicator to guide users.
                </p>

                <h4>Mandatory requirements:</h4>
                <ul>
                  <li><strong>Minimum 8 characters</strong></li>
                  <li><strong>At least one letter</strong> (a-z, A-Z)</li>
                  <li><strong>At least one digit</strong> (0-9)</li>
                </ul>

                <h4>Bonus criteria (recommended):</h4>
                <ul>
                  <li>At least one uppercase letter</li>
                  <li>At least one special character (!@#$%^&*...)</li>
                </ul>

                <h4>Strength levels displayed:</h4>
                <div className="not-prose">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    <div className="p-2 rounded bg-destructive/10 text-destructive text-center text-sm">
                      Incomplete
                    </div>
                    <div className="p-2 rounded bg-amber-500/10 text-amber-600 text-center text-sm">
                      Fair
                    </div>
                    <div className="p-2 rounded bg-emerald-500/10 text-emerald-600 text-center text-sm">
                      Strong
                    </div>
                    <div className="p-2 rounded bg-emerald-600/10 text-emerald-700 text-center text-sm">
                      Very Strong
                    </div>
                  </div>
                </div>

                <h4 className="mt-4">Additional measures:</h4>
                <ul>
                  <li>Encrypted transmission via HTTPS/TLS 1.3</li>
                  <li>Hashing with bcrypt (Supabase Auth)</li>
                  <li>Brute force protection (rate limiting)</li>
                  <li>JWT tokens with short expiration</li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Protection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  3. Data Protection
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <h4>Encryption:</h4>
                <ul>
                  <li><strong>In transit:</strong> TLS 1.3 on all connections</li>
                  <li><strong>At rest:</strong> AES-256 for stored data</li>
                  <li><strong>OAuth tokens:</strong> AES-256-GCM with dedicated encryption key</li>
                </ul>

                <h4>Access control:</h4>
                <ul>
                  <li>Row Level Security (RLS) on all tables</li>
                  <li>Strict user data isolation</li>
                  <li>Principle of least privilege</li>
                  <li>Audit logs for sensitive actions</li>
                </ul>

                <h4>Gmail access (Google API Limited Use):</h4>
                <ul>
                  <li>Readonly scope only</li>
                  <li>No email modification</li>
                  <li>Processing of attachments (documents) only</li>
                  <li>Tokens revocable by user at any time</li>
                </ul>
              </CardContent>
            </Card>

            {/* Disaster Recovery */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-primary" />
                  4. Disaster Recovery (Business Continuity)
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  EasyFinder relies on Supabase infrastructure (AWS eu-west-3, Paris) to ensure 
                  data availability and recovery.
                </p>

                <h4>Recovery objectives:</h4>
                <div className="not-prose">
                  <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="text-2xl font-bold text-primary">RTO</div>
                      <div className="text-sm text-muted-foreground">Recovery Time Objective</div>
                      <div className="mt-2 font-medium">&lt; 4 hours</div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="text-2xl font-bold text-primary">RPO</div>
                      <div className="text-sm text-muted-foreground">Recovery Point Objective</div>
                      <div className="mt-2 font-medium">&lt; 24 hours</div>
                    </div>
                  </div>
                </div>

                <h4 className="mt-4">Backups:</h4>
                <ul>
                  <li><strong>Database:</strong> Automatic daily snapshots (Supabase)</li>
                  <li><strong>Files (Storage):</strong> AWS multi-zone replication</li>
                  <li><strong>Retention:</strong> 7 days of backups</li>
                  <li><strong>Point-in-Time Recovery:</strong> Available for the last 7 days</li>
                </ul>

                <h4>Recovery procedure:</h4>
                <ol>
                  <li>Incident identification and impact assessment</li>
                  <li>Stakeholder notification</li>
                  <li>Restore from last valid snapshot</li>
                  <li>Data integrity verification</li>
                  <li>Service restoration and user communication</li>
                </ol>
              </CardContent>
            </Card>

            {/* Data Breach Response */}
            <Card className="border-destructive/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  5. Data Breach Response
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  In case of a personal data breach, EasyFinder applies a procedure compliant 
                  with GDPR (Articles 33 and 34).
                </p>

                <h4>Regulatory deadlines:</h4>
                <div className="not-prose">
                  <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5">
                      <div className="font-semibold text-destructive">Supervisory Authority</div>
                      <div className="text-sm">Notification within 72h</div>
                      <div className="text-xs text-muted-foreground mt-1">If risk to individuals' rights</div>
                    </div>
                    <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
                      <div className="font-semibold text-amber-600">Users</div>
                      <div className="text-sm">Without undue delay</div>
                      <div className="text-xs text-muted-foreground mt-1">If high risk to affected individuals</div>
                    </div>
                  </div>
                </div>

                <h4 className="mt-4">Summary procedure:</h4>
                <ol>
                  <li><strong>Detection:</strong> Identification and immediate containment</li>
                  <li><strong>Assessment:</strong> Data nature, number of people, severity</li>
                  <li><strong>Authority notification:</strong> Via supervisory authority portal within 72h</li>
                  <li><strong>Communication:</strong> Information to affected users</li>
                  <li><strong>Documentation:</strong> Breach register updated</li>
                  <li><strong>Remediation:</strong> Corrective and preventive measures</li>
                </ol>

                <h4>Emergency contacts:</h4>
                <ul>
                  <li><strong>Security Team:</strong> <a href="mailto:security@easyfinder.fr">security@easyfinder.fr</a></li>
                  <li><strong>DPO:</strong> <a href="mailto:privacy@easyfinder.fr">privacy@easyfinder.fr</a></li>
                </ul>

                <p className="text-sm text-muted-foreground">
                  See full procedure: <a href="/incident-response-policy-en">Incident Response Policy</a>
                </p>
              </CardContent>
            </Card>

            {/* Roles & Responsibilities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  6. Roles and Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <div className="not-prose">
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="font-semibold">Chief Information Security Officer (CISO)</div>
                      <div className="text-sm text-muted-foreground">
                        Definition and enforcement of security policy, incident management
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="font-semibold">Data Protection Officer (DPO)</div>
                      <div className="text-sm text-muted-foreground">
                        GDPR compliance, supervisory authority liaison, data subject rights
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="font-semibold">Technical Team</div>
                      <div className="text-sm text-muted-foreground">
                        Technical measures implementation, monitoring, incident response
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Review */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    This policy is reviewed annually or after any major security incident.
                  </p>
                  <p className="text-sm">
                    <strong>Next scheduled review:</strong> December 2025
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Contact: <a href="mailto:security@easyfinder.fr" className="text-primary hover:underline">security@easyfinder.fr</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default InformationSecurityPolicyEN;
